import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AssessmentFormProps {
  formId: string;
  onComplete: (score: number, passed: boolean) => void;
  onClose: () => void;
}

export const AssessmentForm = ({ formId, onComplete, onClose }: AssessmentFormProps) => {
  const { language } = useLanguage();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{[key: string]: string | string[]}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questions, setQuestions] = useState<any[]>([]);
  const [assessmentForm, setAssessmentForm] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssessmentData();
  }, [formId]);

  const fetchAssessmentData = async () => {
    try {
      setLoading(true);
      
      // Fetch assessment form
      const { data: form, error: formError } = await supabase
        .from('assessment_forms')
        .select('*')
        .eq('id', formId)
        .single();

      if (formError) throw formError;
      setAssessmentForm(form);

      // Fetch questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('assessment_questions')
        .select('*')
        .eq('form_id', formId)
        .order('order_index');

      if (questionsError) throw questionsError;
      setQuestions(questionsData || []);
    } catch (error) {
      console.error('Error fetching assessment data:', error);
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center">Loading assessment...</div>;
  }

  if (questions.length === 0) {
    return <div className="text-center">No questions available for this assessment.</div>;
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswerSelect = (value: string) => {
    if (currentQ.question_type === 'single') {
      setAnswers(prev => ({
        ...prev,
        [currentQ.id]: value
      }));
    } else {
      // Multiple choice handling
      const currentAnswers = (answers[currentQ.id] as string[]) || [];
      const newAnswers = currentAnswers.includes(value)
        ? currentAnswers.filter(a => a !== value)
        : [...currentAnswers, value];
      
      setAnswers(prev => ({
        ...prev,
        [currentQ.id]: newAnswers
      }));
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('User authenticated:', user.id);
      console.log('Form ID:', formId);
      console.log('Questions:', questions);
      console.log('Answers:', answers);

      // Calculate score
      let totalPoints = 0;
      let earnedPoints = 0;

      questions.forEach(question => {
        console.log('Processing question:', question);
        if (!question.points) {
          console.error('Question missing points:', question);
          throw new Error('Question data is invalid - missing points');
        }
        if (!question.options || !question.options.correct_answers) {
          console.error('Question missing correct answers:', question);
          throw new Error('Question data is invalid - missing correct answers');
        }
        
        totalPoints += question.points;
        const userAnswer = answers[question.id];
        const correctAnswers = question.options.correct_answers;

        if (question.question_type === 'single') {
          if (userAnswer && correctAnswers.includes(userAnswer)) {
            earnedPoints += question.points;
          }
        } else {
          // Multiple choice - give partial credit
          const userAnswerArray = userAnswer as string[] || [];
          const correctCount = userAnswerArray.filter(a => correctAnswers.includes(a)).length;
          const incorrectCount = userAnswerArray.filter(a => !correctAnswers.includes(a)).length;
          
          // Only award points if all correct answers are selected and no incorrect ones
          if (correctCount === correctAnswers.length && incorrectCount === 0) {
            earnedPoints += question.points;
          }
        }
      });
      
      const score = Math.round((earnedPoints / totalPoints) * 100);
      const passed = score >= (assessmentForm?.pass_percentage || 70);
      
      console.log('Calculated score:', score, 'Passed:', passed);
      console.log('Total points:', totalPoints, 'Earned:', earnedPoints);
      
      const insertData = {
        user_id: user.id,
        form_id: formId,
        total_points: earnedPoints,
        max_points: totalPoints,
        percentage_score: score,
        passed: passed,
        completed_at: new Date().toISOString()
      };
      
      console.log('Inserting data:', insertData);
      
      // First, check if we can access the table
      const { data: testData, error: testError } = await supabase
        .from('assessment_results')
        .select('id')
        .limit(1);
      
      if (testError) {
        console.error('Cannot access assessment_results table:', testError);
        throw new Error(`Table access error: ${testError.message}`);
      }
      
      console.log('Table is accessible, proceeding with insert...');
      
      // Save assessment result to database
      const { data: insertedData, error: insertError } = await supabase
        .from('assessment_results')
        .insert(insertData)
        .select();

      if (insertError) {
        console.error('Database insert error:', insertError);
        console.error('Error code:', insertError.code);
        console.error('Error message:', insertError.message);
        console.error('Error details:', insertError.details);
        console.error('Error hint:', insertError.hint);
        throw new Error(`Database error: ${insertError.message}`);
      }
      
      console.log('Inserted data:', insertedData);
      
      console.log('Assessment saved successfully!');
      toast.success(`Assessment completed! Score: ${score}%`);
      onComplete(score, passed);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error(`Failed to submit assessment: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {language === 'en' ? 'Assessment Form' : 'Φόρμα Αξιολόγησης'}
          </CardTitle>
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-muted-foreground">
            {language === 'en' 
              ? `Question ${currentQuestion + 1} of ${questions.length}`
              : `Ερώτηση ${currentQuestion + 1} από ${questions.length}`
            }
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-4">
              {language === 'en' ? currentQ.question_en : currentQ.question_el}
            </h3>
            
            {currentQ.question_type === 'single' ? (
              <RadioGroup
                value={(answers[currentQ.id] as string) || ''}
                onValueChange={handleAnswerSelect}
                className="space-y-3"
              >
                {currentQ.options.choices.map((choice) => (
                  <div key={choice.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={choice.id} id={choice.id} />
                    <Label htmlFor={choice.id} className="cursor-pointer">
                      {language === 'en' ? choice.text_en : choice.text_el}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            ) : (
              <div className="space-y-3">
                {currentQ.options.choices.map((choice) => (
                  <div key={choice.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={choice.id}
                      checked={((answers[currentQ.id] as string[]) || []).includes(choice.id)}
                      onCheckedChange={() => handleAnswerSelect(choice.id)}
                    />
                    <Label htmlFor={choice.id} className="cursor-pointer">
                      {language === 'en' ? choice.text_en : choice.text_el}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
            >
              {language === 'en' ? 'Previous' : 'Προηγούμενο'}
            </Button>

            {currentQuestion === questions.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={!answers[currentQ.id] || 
                  (Array.isArray(answers[currentQ.id]) && (answers[currentQ.id] as string[]).length === 0) ||
                  isSubmitting}
              >
                {isSubmitting 
                  ? (language === 'en' ? 'Submitting...' : 'Υποβολή...')
                  : (language === 'en' ? 'Submit' : 'Υποβολή')
                }
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!answers[currentQ.id] || 
                  (Array.isArray(answers[currentQ.id]) && (answers[currentQ.id] as string[]).length === 0)}
              >
                {language === 'en' ? 'Next' : 'Επόμενο'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};